import { Basemap, BasemapId } from './types';
import { API_CONFIG } from './apiConfig';

// AGB basemap tiles are precomputed by scripts/generate-agb-basemap.py and
// hosted as a plain XYZ pyramid on S3. Replaces an earlier compute-backed
// Earthmover Flux endpoint that required tile-fetching through an
// Authorization header (removed along with this change — the S3 tiles
// serve ~10–50× faster and don't require auth or a custom tile layer).
const agbTileUrl = `${API_CONFIG.s3.baseUrl}/basemaps/agb_100m/{z}/{x}/{y}.png`;

export const BASEMAPS: Record<BasemapId, Basemap> = {
  stadia_satellite: {
    id: 'stadia_satellite',
    displayName: 'Stadia Alidade Satellite',
    description: 'High-resolution satellite imagery. Best for visual context over forested terrain.',
    kind: 'imagery',
    thumbnailPath: '/basemaps/stadia_satellite.jpg',
    url: `https://tiles.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}.jpg?api_key=${API_CONFIG.stadiaMaps.apiKey}`,
    attribution: '© <a href="https://stadiamaps.com/">Stadia Maps</a>, © <a href="https://openmaptiles.org/">OpenMapTiles</a>',
    // Cap zoom at 17. Stadia's Alidade Satellite coverage starts thinning
    // at z=18 over rural / forested terrain (most of the SFI demo's
    // study area), and connections to the CDN drop with
    // ERR_CONNECTION_CLOSED for missing tiles. The per-crown cinematic
    // fly only needs z=16-17 (basin flyZoom + 2), so capping here
    // doesn't affect the demo — just prevents users from pinch-zooming
    // past where tiles get flaky.
    maxZoom: 17,
  },
  earthmover_agb_100m: {
    id: 'earthmover_agb_100m',
    displayName: 'Above Ground Biomass (100m, 2025)',
    description: 'Global CTrees aboveground biomass at 100m resolution. Color ramp: gist_earth. Useful for scanning biomass gradients at country-to-regional scales.',
    kind: 'thematic',
    thumbnailPath: '/basemaps/earthmover_agb_100m.png',
    url: agbTileUrl,
    attribution: 'Aboveground Biomass © <a href="https://ctrees.org/">CTrees</a>',
    opacity: 0.7,
    // Tiles are scraped from Earthmover Flux for z=5..10 and served from
    // S3. Flux rejects tiles below z=5 with HTTP 413 ("too big") because
    // the 100 m source would require too many pixels per tile at wide
    // zooms — once we have direct Arraylake access we can render z=3/4
    // locally, but today the cached pyramid starts at z=5. z=10 is the
    // top of the pyramid (100 m source is already oversampled there); we
    // surface that as `maxNativeZoom` so Leaflet upscales the z=10 tiles
    // past z=10 (pixelated, but the user can keep zooming in to inspect
    // higher-detail data layers stacked on top) instead of 404-ing on
    // tiles that don't exist.
    minZoom: 5,
    maxNativeZoom: 10,
    // Default the DetailView data layer to AGB when this basemap is active,
    // so the left-panel data matches what the user is already seeing.
    preferredLayerId: 'aboveground_biomass',
  },
};

export const DEFAULT_BASEMAP_ID: BasemapId = 'stadia_satellite';

export const isBasemapId = (value: unknown): value is BasemapId =>
  typeof value === 'string' && value in BASEMAPS;
